import { useEffect, useState } from 'react';
import { Spin, Alert, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { mappingApi } from '../../services/api/mapping';
import {
  MappingVerificationWizard,
  type VerificationResult,
  type DocType,
  type FieldVerification,
  type DocTypeDataField,
} from '@ute/mapper-ui-components';
import { createMappingApiAdapter } from '../../adapters/mappingApiAdapter';
import { AdvancedOptionsPanel } from '../authoring/AdvancedOptionsPanel';
import { FieldToolsPanel } from '../authoring/FieldToolsPanel';
import { useBoxEditor } from '../authoring/useBoxEditor';

interface MappingVerificationProps {
  mappingKey: string;
  pdfHash: string;
  pdfUrl?: string;
  docTypeId: string;
  tenantId?: string;
  /** When true, MAPPER-only authoring slots (Advanced tab, field tools) are shown. */
  editingEnabled?: boolean;
}

interface MappingData {
  status: string;
  fields: Record<string, any>;
  dateFormat: string;
  data: any;
  blocks?: any[];
  width?: number;
  height?: number;
}

export function MappingVerification({
  mappingKey,
  pdfHash,
  pdfUrl,
  docTypeId,
  tenantId,
  editingEnabled,
}: MappingVerificationProps) {
  const navigate = useNavigate();
  const api = createMappingApiAdapter();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mappingData, setMappingData] = useState<MappingData | null>(null);
  const [docType, setDocType] = useState<DocType | null>(null);
  // templateData is the mapping.data object — needed by FieldToolsPanel to read
  // current coords/patterns and by AdvancedOptionsPanel to pre-populate the form.
  const [templateData, setTemplateData] = useState<
    Record<string, any> | undefined
  >(undefined);
  // Accumulates unsaved Advanced panel edits so they survive tab switches
  // and flow into the wizard as the single controlled dateFormat prop.
  const [liveAdvancedValues, setLiveAdvancedValues] = useState<
    Record<string, string | undefined>
  >({});
  // Raw PDF text for pattern match-count — fetched once when editing starts and
  // shared across all field tool panels. undefined = not loaded yet.
  const [pdfText, setPdfText] = useState<string | undefined>(undefined);

  // Box editor — shared state between the Advanced-tab controls and PDF overlay.
  const boxEditor = useBoxEditor({
    mappingKey,
    pdfHash,
    templateData,
    tenantId,
    liveAdvancedValues,
    onSaved: () => refreshAfterSave(),
    blocks: mappingData?.blocks,
    pageWidth: mappingData?.width,
    pageHeight: mappingData?.height,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [docTypeRes, mappingRes] = await Promise.all([
        mappingApi.getDocType(docTypeId, tenantId),
        mappingApi.getMappingData(mappingKey, pdfHash, tenantId),
      ]);
      setDocType(docTypeRes.data);
      setMappingData(mappingRes.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Silent refresh after save — reloads extraction data + template data without
  // blanking the wizard (no loading spinner).
  const refreshAfterSave = async () => {
    try {
      const [mappingRes, templateRes] = await Promise.all([
        mappingApi.getMappingData(mappingKey, pdfHash, tenantId),
        mappingApi.get(mappingKey),
      ]);
      setMappingData(mappingRes.data);
      setTemplateData((templateRes.data as any)?.data ?? {});
    } catch {
      // non-fatal — stale data is better than an error screen
    }
  };

  useEffect(() => {
    fetchData();
  }, [mappingKey, pdfHash, docTypeId]);

  // Fetch raw template data (mapping.data coords/patterns) for authoring panels
  useEffect(() => {
    if (!editingEnabled) return;
    mappingApi
      .get(mappingKey)
      .then((res) => setTemplateData((res.data as any)?.data ?? {}))
      .catch(() => {
        /* non-fatal */
      });
  }, [mappingKey, editingEnabled]);

  // Fetch raw PDF text once for pattern match-count, shared across all field panels.
  useEffect(() => {
    if (!editingEnabled) return;
    mappingApi
      .getPdfText(mappingKey, pdfHash, tenantId)
      .then((res) => setPdfText(res.data.text || ''))
      .catch(() => setPdfText(''));
  }, [mappingKey, pdfHash, tenantId, editingEnabled]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !docType) {
    return (
      <Alert
        description={error || 'Failed to load DocType'}
        type="error"
        showIcon
      />
    );
  }

  return (
    <MappingVerificationWizard
      mappingKey={mappingKey}
      pdfHash={pdfHash}
      pdfUrl={pdfUrl || ''}
      docType={docType}
      api={api}
      mappingStatus={mappingData?.status}
      initialData={mappingData}
      onComplete={(result: VerificationResult) => {
        message.success('Mapping verified successfully');
        navigate(`/mappings/${mappingKey}`, {
          state: { updatedMapping: result },
        });
      }}
      onError={(error: Error) => {
        message.error('Failed to verify mapping. Please try again.');
        console.error('Verification failed:', error);
      }}
      dateFormat={liveAdvancedValues.dateFormat}
      // MAPPER-only PDF overlay for box editing (no-op unless edit toggle is on)
      pdfOverlay={editingEnabled ? boxEditor.renderOverlay : undefined}
      suppressHighlight={editingEnabled && boxEditor.focusMode}
      // MAPPER-only authoring slots — undefined for external embeds
      advancedTabContent={
        editingEnabled ? (
          <>
            {boxEditor.controls}
            <AdvancedOptionsPanel
              mappingKey={mappingKey}
              tenantId={tenantId}
              templateData={templateData}
              liveValues={liveAdvancedValues}
              onFieldChange={(field, value) => {
                setLiveAdvancedValues((prev) => ({ ...prev, [field]: value }));
              }}
              onSaved={() => refreshAfterSave()}
            />
          </>
        ) : undefined
      }
      fieldToolsPanel={
        editingEnabled
          ? (
              fieldKey: string,
              fieldVer: FieldVerification,
              docTypeField: DocTypeDataField | undefined,
              resolveBox: () => { x: number; y: number; w: number; h: number } | null
            ) => (
              <FieldToolsPanel
                mappingKey={mappingKey}
                pdfHash={pdfHash}
                fieldKey={fieldKey}
                field={fieldVer}
                docTypeField={docTypeField}
                tenantId={tenantId}
                templateData={templateData}
                liveAdvancedValues={liveAdvancedValues}
                onSaved={() => refreshAfterSave()}
                onActivateBox={(fk, seedBox) =>
                  boxEditor.activateField(fk, seedBox ?? undefined)
                }
                onDeactivateBox={boxEditor.deactivateField}
                onSetBoxPage={boxEditor.setFieldPage}
                onDiscardBox={boxEditor.discardField}
                resolveBox={resolveBox}
                getBoxEdit={boxEditor.getFieldCoords}
                pdfText={pdfText}
              />
            )
          : undefined
      }
    />
  );
}
